/**
 * Step 6 per-competency capture (HG-3A G3-02).
 *
 * Reading order, per the approved design and Change Spec v1.3 section 4.2:
 *   1. performance criteria, read-only, from the registry
 *   2. the three dimensions, coded, worst to best by registry position
 *   3. the limiting factor, coded, never derived
 *   4. the proposal with the rule statement (GCH-13: "shall show the rule")
 *   5. the grade itself stays where it already is, outside this panel
 *   6. the override record, only when grade and proposal disagree
 *   7. the per-competency development level and its source (G3-07, ICD 3.1.4)
 *
 * Everything coded here is an ICD enum code. No control in this panel accepts
 * free text where a code is required (GCH-11), and the single free-text field
 * exists only for the `other` override category (DEC-28).
 */

import {
  DEVELOPMENT_LEVEL_SOURCE_LABEL,
  DIMENSION_KEYS,
  NOT_RECORDED_LABEL,
  WEAKEST_DIMENSION_RULE_STATEMENT,
  dimensionOptions,
  proposeGrade,
  type DimensionKey,
  type ResolvedFramework,
} from '@orca360/shared';

const DIMENSION_LABEL: Record<DimensionKey, string> = {
  how_many: 'How many',
  how_often: 'How often',
  tem_outcome: 'TEM outcome',
};

const DIMENSION_HINT: Record<DimensionKey, string> = {
  how_many: 'Acquisition: how many of the competency’s behaviours were observed.',
  how_often: 'Robustness: how consistently they were demonstrated.',
  tem_outcome: 'Effectiveness: what the performance did to safety.',
};

const OVERRIDE_CATEGORIES: Array<{ code: string; label: string }> = [
  { code: 'evidence_not_captured_by_dimensions', label: 'Evidence not captured by the dimensions' },
  { code: 'dimension_recorded_in_error', label: 'Dimension recorded in error' },
  { code: 'tem_outcome_not_attributable', label: 'TEM outcome not attributable to the trainee' },
  { code: 'other', label: 'Other' },
];

const DEVELOPMENT_LEVELS: Array<{ code: string; label: string }> = [
  { code: 'beginner', label: 'Beginner' },
  { code: 'building_competencies', label: 'Building Competencies' },
  { code: 'proficient', label: 'Proficient' },
];

/**
 * The ICD 8.8 sources this panel may RECORD.
 *
 * `default_mapping` is absent, and its absence is the point (G3-14b). It is the
 * automated-recommendation rung, and nothing automated records a level here:
 * every value this panel writes is an instructor act, whether the instructor
 * chose it or confirmed the suggestion by saving it. Offering the token would
 * be offering an instructor the ability to sign someone else's name.
 *
 * A row that already stores it, from before this change, still displays it: see
 * the disabled option rendered beside this list.
 */
const DEVELOPMENT_LEVEL_SOURCES: Array<{ code: string; label: string }> = [
  { code: 'instructor_override', label: 'Instructor override' },
  { code: 'operator_setting', label: 'Operator setting' },
];

export interface CapturedValues {
  how_many?: string | null;
  how_often?: string | null;
  tem_outcome?: string | null;
  limiting_factor?: string | null;
  proposed_grade?: number | null;
  override_flag?: boolean | null;
  override_category?: string | null;
  override_note?: string | null;
  development_level?: string | null;
  development_level_source?: string | null;
  historical?: boolean | null;
  performance_criteria?: unknown;
}

export interface CompetencyCapturePanelProps {
  competency: string;
  /** The instructor's recorded grade, owned by the existing grade control. */
  recordedGrade: number | null;
  values: CapturedValues;
  framework: ResolvedFramework | null;
  /** Read-only until the editor is in edit mode. */
  disabled?: boolean;
  /**
   * The ICD 8.8 code suggested from the session development level (G3-14b), or
   * null for no suggestion. The caller decides whether it applies to this row;
   * developmentLevelPrefillApplies in session-assessment-draft.ts is that
   * decision, shared with the save so the two cannot disagree.
   *
   * A suggestion, never data. It is shown only while the panel is editable, is
   * styled as a proposal rather than a record, and reaches the database only if
   * the instructor saves this competency.
   */
  prefillDevelopmentLevel?: string | null;
  onChange: (patch: CapturedValues) => void;
  /** Historical rows only: begin capturing dimensions on an old assessment. */
  onBeginHistoricalCapture?: () => void;
}

interface PerformanceCriterion {
  behaviour_code?: string;
  conditions?: unknown;
  competency_standard?: unknown;
}

function asCriteriaArray(value: unknown): PerformanceCriterion[] {
  return Array.isArray(value) ? (value as PerformanceCriterion[]) : [];
}

function renderCriterionText(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const text = (value as Record<string, unknown>).text;
    if (typeof text === 'string') return text;
  }
  return null;
}

export function CompetencyCapturePanel({
  competency,
  recordedGrade,
  values,
  framework,
  disabled = false,
  prefillDevelopmentLevel = null,
  onChange,
  onBeginHistoricalCapture,
}: CompetencyCapturePanelProps) {
  const proposal = proposeGrade(framework, {
    how_many: values.how_many,
    how_often: values.how_often,
    tem_outcome: values.tem_outcome,
  });

  const isOverride =
    recordedGrade != null && proposal.proposedGrade != null && recordedGrade !== proposal.proposedGrade;

  const criteria = asCriteriaArray(values.performance_criteria);

  // A historical row (ICD 9.3) carries no dimensions and must never be
  // back-filled silently. GCH-35: present as "not recorded", never as zero or a
  // default. Adding them is permitted, but only as a deliberate act.
  const isHistoricalWithoutCapture =
    Boolean(values.historical) && !values.how_many && !values.how_often && !values.tem_outcome;

  // G3-14b. The suggestion is offered only where it can still be acted on: a
  // read-only panel would present it as a recorded value, and a historical row
  // is never prefilled at all (GCH-35). While it stands, the source reads
  // instructor_override because that is what saving it would record.
  const prefilledLevel =
    !disabled && !values.historical && values.development_level == null
      ? (prefillDevelopmentLevel ?? null)
      : null;
  const shownLevel = values.development_level ?? prefilledLevel;
  const shownSource =
    values.development_level_source ?? (prefilledLevel ? 'instructor_override' : null);
  // A row stored before this change may carry a source the panel no longer
  // offers. It is shown, disabled, rather than falling off the control: a blank
  // where the database holds a value is the divergence this file keeps fixing.
  const storedSourceNotOffered =
    shownSource != null && !DEVELOPMENT_LEVEL_SOURCES.some((s) => s.code === shownSource);
  // Bound explicitly, because the caption is no longer wrapped around the
  // control: see the comment on that markup for why it must not be.
  const levelSelectId = `capture-${competency}-development-level`;

  const selectClass =
    'w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <section className="mb-4 rounded-lg border border-slate-200 bg-white" data-testid={`capture-${competency}`}>
      <header className="border-b border-slate-200 px-4 py-2">
        <h3 className="text-sm font-semibold text-navy">Assessment dimensions</h3>
      </header>

      <div className="space-y-4 px-4 py-3">
        {/* 1. Performance criteria, read from the registry (GCH-23, CFX-11). */}
        {criteria.length > 0 && (
          <details className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
            <summary className="cursor-pointer text-xs font-semibold text-slate-700">
              Performance criteria ({criteria.length})
            </summary>
            <ul className="mt-2 space-y-2">
              {criteria.map((c, i) => (
                <li key={`${c.behaviour_code ?? 'criterion'}-${i}`} className="text-xs text-slate-700">
                  <span className="font-mono font-semibold">{c.behaviour_code}</span>
                  {renderCriterionText(c.conditions) && (
                    <div>
                      <span className="font-semibold">Conditions:</span> {renderCriterionText(c.conditions)}
                    </div>
                  )}
                  {renderCriterionText(c.competency_standard) && (
                    <div>
                      <span className="font-semibold">Standard:</span>{' '}
                      {renderCriterionText(c.competency_standard)}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* Historical banner (ICD 9.3, GCH-35). */}
        {isHistoricalWithoutCapture && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-semibold">Dimensions {NOT_RECORDED_LABEL.toLowerCase()}</p>
            <p className="mt-1">
              This assessment was recorded before the assessment dimensions existed. Its grade stands as
              issued; the dimensions were never captured and are not inferred.
            </p>
            {!disabled && onBeginHistoricalCapture && (
              <button
                type="button"
                onClick={onBeginHistoricalCapture}
                className="mt-2 rounded border border-amber-400 bg-white px-2 py-1 font-semibold text-amber-900 hover:bg-amber-100"
              >
                Add dimensions to this assessment
              </button>
            )}
          </div>
        )}

        {/* 2. The three dimensions, worst to best by registry position. */}
        <div className="grid gap-3 sm:grid-cols-3">
          {DIMENSION_KEYS.map((key) => {
            const options = dimensionOptions(framework, key);
            return (
              <label key={key} className="block text-xs">
                <span className="font-semibold text-navy">{DIMENSION_LABEL[key]}</span>
                <span className="mt-0.5 block text-[11px] text-slate-500">{DIMENSION_HINT[key]}</span>
                <select
                  className={`mt-1 ${selectClass}`}
                  disabled={disabled || options.length === 0}
                  value={values[key] ?? ''}
                  onChange={(e) => onChange({ [key]: e.target.value || null })}
                  aria-label={DIMENSION_LABEL[key]}
                >
                  <option value="">{NOT_RECORDED_LABEL}</option>
                  {options.map((o) => (
                    <option key={o.code} value={o.code} title={o.wordPicture}>
                      {o.position}. {o.wordPicture}
                    </option>
                  ))}
                </select>
                {options.length === 0 && (
                  <span className="mt-1 block text-[11px] text-slate-400">
                    No scale published for this framework version.
                  </span>
                )}
              </label>
            );
          })}
        </div>

        {/* 3. Limiting factor. Never derived (ICD 8.7). */}
        <label className="block text-xs">
          <span className="font-semibold text-navy">Limiting factor</span>
          <span className="mt-0.5 block text-[11px] text-slate-500">
            {proposal.tiedAtMinimum.length > 1
              ? 'Two or more dimensions are equally lowest. Record which one drove the grade.'
              : 'Which dimension set the grade. Stored as recorded, never recomputed.'}
          </span>
          <select
            className={`mt-1 ${selectClass}`}
            disabled={disabled}
            value={values.limiting_factor ?? ''}
            onChange={(e) => onChange({ limiting_factor: e.target.value || null })}
            aria-label="Limiting factor"
          >
            <option value="">{NOT_RECORDED_LABEL}</option>
            {DIMENSION_KEYS.map((key) => (
              <option key={key} value={key}>
                {DIMENSION_LABEL[key]}
              </option>
            ))}
          </select>
        </label>

        {/* 4. The proposal and the rule (GCH-13: the rule "shall" be shown). */}
        <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2">
          {proposal.complete ? (
            <p className="text-sm text-sky-900">
              <span className="font-semibold">Proposed grade: {proposal.proposedGrade}</span>
            </p>
          ) : (
            <p className="text-sm text-sky-900">
              Record all three dimensions to see a proposed grade.
            </p>
          )}
          <p className="mt-1 text-[11px] text-sky-800">{WEAKEST_DIMENSION_RULE_STATEMENT}</p>
        </div>

        {/* 5. Override record, only when the two disagree (GCH-13, DEC-28). */}
        {isOverride && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2">
            <p className="text-xs font-semibold text-amber-900">
              Recorded grade {recordedGrade} differs from the proposal {proposal.proposedGrade}
            </p>
            <label className="mt-2 block text-xs">
              <span className="font-semibold text-amber-900">Reason (required)</span>
              <select
                className={`mt-1 ${selectClass}`}
                disabled={disabled}
                value={values.override_category ?? ''}
                onChange={(e) =>
                  onChange({
                    override_category: e.target.value || null,
                    // A note belongs to `other` alone; drop it on any other choice.
                    override_note: e.target.value === 'other' ? (values.override_note ?? null) : null,
                  })
                }
                aria-label="Override reason"
              >
                <option value="">Select a reason</option>
                {OVERRIDE_CATEGORIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            {values.override_category === 'other' && (
              <label className="mt-2 block text-xs">
                <span className="font-semibold text-amber-900">Explanation (required for “other”)</span>
                <textarea
                  className={`mt-1 ${selectClass}`}
                  rows={2}
                  disabled={disabled}
                  value={values.override_note ?? ''}
                  onChange={(e) => onChange({ override_note: e.target.value || null })}
                  aria-label="Override explanation"
                />
              </label>
            )}
            <p className="mt-2 text-[11px] text-amber-800">
              The recorded grade is always yours. No explanation is required on any reason but “other”.
            </p>
          </div>
        )}

        {/* 6. Per-competency development level and source (G3-07, ICD 3.1.4).
            The level may open PREFILLED from the session development level
            (G3-14b): a starting value, shown as a proposal in the styling the
            proposed grade already uses, and recorded only if this competency is
            saved. Guide writes no automatic default anywhere (decision B). */}
        <div className="grid gap-3 sm:grid-cols-2">
          {/* Not a <label> wrapper, deliberately. A click anywhere inside a
              label is forwarded to the control it labels, so a button placed
              in one opens the select instead of pressing the button, and the
              open list then covers it. The caption is bound with htmlFor, and
              the action sits on the caption line where the open list cannot
              reach it. */}
          <div className="text-xs">
            <div className="flex items-center justify-between gap-2">
              <label htmlFor={levelSelectId} className="font-semibold text-navy">
                Development level
              </label>
              {prefilledLevel && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      development_level: prefilledLevel,
                      development_level_source: 'instructor_override',
                    })
                  }
                  className="rounded border border-sky-400 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-900 hover:bg-sky-100"
                >
                  Record this level
                </button>
              )}
            </div>
            <select
              id={levelSelectId}
              className={`mt-1 ${selectClass} ${
                prefilledLevel ? 'border-sky-300 bg-sky-50 text-sky-900' : ''
              }`}
              disabled={disabled}
              value={shownLevel ?? ''}
              onChange={(e) =>
                onChange({
                  development_level: e.target.value || null,
                  // An instructor choosing the level is, by definition, the
                  // instructor_override rung of the ICD 8.8 source order.
                  development_level_source: e.target.value ? 'instructor_override' : null,
                })
              }
              aria-label="Development level"
            >
              <option value="">{NOT_RECORDED_LABEL}</option>
              {DEVELOPMENT_LEVELS.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}
                </option>
              ))}
            </select>
            {prefilledLevel && (
              <p className="mt-1 rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-[11px] text-sky-800">
                Suggested from the session development level. Nothing is recorded until you save
                this competency: record it as it stands, change it, or clear it.
              </p>
            )}
          </div>
          <label className="block text-xs">
            <span className="font-semibold text-navy">Level source</span>
            <select
              className={`mt-1 ${selectClass} ${
                prefilledLevel ? 'border-sky-300 bg-sky-50 text-sky-900' : ''
              }`}
              // While the level is only a suggestion the source is not the
              // instructor's to choose: what a save would record is the
              // instructor's own act, and it says so. It becomes editable again
              // as soon as a level is actually recorded.
              disabled={disabled || prefilledLevel != null}
              value={shownSource ?? ''}
              onChange={(e) => onChange({ development_level_source: e.target.value || null })}
              aria-label="Development level source"
            >
              <option value="">{NOT_RECORDED_LABEL}</option>
              {DEVELOPMENT_LEVEL_SOURCES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
              {storedSourceNotOffered && (
                <option value={shownSource as string} disabled>
                  {DEVELOPMENT_LEVEL_SOURCE_LABEL[shownSource as string] ?? shownSource}
                </option>
              )}
            </select>
          </label>
        </div>
      </div>
    </section>
  );
}

export default CompetencyCapturePanel;
